import type {
  RtaLinearFit,
  RtaPoint,
} from './rtaCalculations';

export interface RtaEngineeringParams {
  /**
   * 基质渗透率
   * mD
   */
  permeabilityMd: number | null;

  /**
   * 有效厚度
   * m
   */
  netPayM: number | null;

  /**
   * 孔隙度
   * 支持 0.08 或 8
   */
  porosity: number | null;

  /**
   * 含水饱和度
   * 支持 0.30 或 30
   */
  waterSaturation: number | null;

  /**
   * 水平段长度
   * m
   */
  lateralLengthM: number | null;

  /**
   * 裂缝半长
   * m
   */
  fractureHalfLengthM: number | null;

  /**
   * 裂缝导流能力
   * D·cm
   */
  fractureConductivityDcm: number | null;

  /**
   * 数据准备中录入的 SRV
   * m3
   */
  inputSrvM3: number | null;
}

export interface RtaEngineeringResult {
  linearFlowStartDay: number | null;
  linearFlowEndDay: number | null;
  linearFlowDurationDay: number | null;

  startRate: number | null;
  endRate: number | null;
  rateDeclinePct: number | null;

  intervalGas: number | null;

  fitCount: number;
  fitR2: number | null;
  fitSlope: number | null;

  stimulatedWidthM: number | null;

  geometricSrvM3: number | null;
  inputSrvM3: number | null;
  srvRatio: number | null;

  effectiveGasPoreVolumeM3: number | null;

  dimensionlessFractureConductivity: number | null;
}

export function normalizeFraction(
  value: number | null,
): number | null {
  if (
    value === null ||
    !Number.isFinite(value) ||
    value < 0
  ) {
    return null;
  }

  if (value <= 1) {
    return value;
  }

  if (value <= 100) {
    return value / 100;
  }

  return null;
}

export function calculateRtaEngineering(
  params: RtaEngineeringParams,
  fitPoints: RtaPoint[],
  fit: RtaLinearFit | null,
): RtaEngineeringResult {
  const first =
    fitPoints.length > 0
      ? fitPoints[0]
      : null;

  const last =
    fitPoints.length > 0
      ? fitPoints[
          fitPoints.length - 1
        ]
      : null;

  const linearFlowStartDay =
    first?.t ?? null;

  const linearFlowEndDay =
    last?.t ?? null;

  const linearFlowDurationDay =
    first && last
      ? Math.max(
          0,
          last.t - first.t,
        )
      : null;

  const startRate =
    first?.q ?? null;

  const endRate =
    last?.q ?? null;

  const rateDeclinePct =
    startRate !== null &&
    endRate !== null &&
    startRate > 0
      ? (
          (
            startRate -
            endRate
          ) /
          startRate
        ) * 100
      : null;

  const intervalGas =
    first && last
      ? Math.max(
          0,
          last.gp -
            first.gp,
        )
      : null;

  const k =
    validPositive(
      params.permeabilityMd,
    );

  const h =
    validPositive(
      params.netPayM,
    );

  const lateral =
    validPositive(
      params.lateralLengthM,
    );

  const xf =
    validPositive(
      params.fractureHalfLengthM,
    );

  const fc =
    validPositive(
      params.fractureConductivityDcm,
    );

  const inputSrv =
    validPositive(
      params.inputSrvM3,
    );

  const phi =
    normalizeFraction(
      params.porosity,
    );

  const sw =
    normalizeFraction(
      params.waterSaturation,
    );

  /*
   * 两翼裂缝对应的总改造宽度近似：
   *
   * W ≈ 2 xf
   */
  const stimulatedWidthM =
    xf !== null
      ? 2 * xf
      : null;

  /*
   * 几何盒状 SRV 近似：
   *
   * V = Lh × 2xf × h
   *
   * 注意：
   * 这里是几何一致性检查，
   * 不是声称 RTA 唯一反演出了 SRV。
   */
  const geometricSrvM3 =
    lateral !== null &&
    xf !== null &&
    h !== null
      ? lateral *
        2 *
        xf *
        h
      : null;

  /*
   * 数据准备中 SRV 与
   * 几何估算 SRV 的比值
   */
  const srvRatio =
    inputSrv !== null &&
    geometricSrvM3 !==
      null &&
    geometricSrvM3 > 0
      ? inputSrv /
        geometricSrvM3
      : null;

  /*
   * 用已有 SRV 优先；
   * 若没有，则使用几何 SRV。
   */
  const referenceSrv =
    inputSrv ??
    geometricSrvM3;

  /*
   * 有效含气孔隙体积：
   *
   * Vgp =
   * SRV × φ × (1-Sw)
   */
  const effectiveGasPoreVolumeM3 =
    referenceSrv !== null &&
    phi !== null &&
    sw !== null
      ? referenceSrv *
        phi *
        Math.max(
          0,
          1 - sw,
        )
      : null;

  /*
   * CfD =
   * Fc / (k × xf)
   *
   * 当前：
   * Fc = D·cm
   * k  = mD
   * xf = m
   *
   * 1 D·cm
   * = 1000 mD × 0.01 m
   * = 10 mD·m
   *
   * 所以：
   *
   * CfD =
   * Fc(D·cm) × 10
   * / [k(mD) × xf(m)]
   */
  const dimensionlessFractureConductivity =
    fc !== null &&
    k !== null &&
    xf !== null
      ? (
          fc * 10
        ) /
        (
          k * xf
        )
      : null;

  return {
    linearFlowStartDay,
    linearFlowEndDay,
    linearFlowDurationDay,

    startRate,
    endRate,
    rateDeclinePct,

    intervalGas,

    fitCount:
      fit?.count ??
      fitPoints.length,

    fitR2:
      fit?.r2 ?? null,

    fitSlope:
      fit?.slope ??
      null,

    stimulatedWidthM,

    geometricSrvM3,
    inputSrvM3:
      inputSrv,

    srvRatio,

    effectiveGasPoreVolumeM3,

    dimensionlessFractureConductivity,
  };
}

function validPositive(
  value: number | null,
): number | null {
  if (
    value === null ||
    !Number.isFinite(value) ||
    value <= 0
  ) {
    return null;
  }

  return value;
}